import React, { useState } from 'react'
import { Stack, Grid, Box, Typography, Chip, Divider, Avatar, Button, IconButton, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  OpenInNew as OpenInNewIcon,
  CalendarToday as CalendarIcon,
  AutoAwesome as AutoAwesomeIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material'
import JobActionsButton from '../JobActionsButton'
import ApplicationDrawer from '../ApplicationDrawer'
import { formatSalary, getPracticeTypeString } from './helpers'
import DOMPurify from 'dompurify'
import { convertNewlinesToBreaks } from '/src/pages/Jobs/helpers'
import useJobFeedback from './JobFeedback'

const getMatchTierColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent':
      return {
        bg: theme.palette.success.light,
        color: theme.palette.success.dark,
        border: theme.palette.success.main,
      }
    case 'strong':
      return {
        bg: theme.palette.info.light,
        color: theme.palette.info.dark,
        border: theme.palette.info.main,
      }
    case 'good':
      return {
        bg: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        border: theme.palette.warning.main,
      }
    case 'moderate':
    default:
      return {
        bg: theme.palette.grey[200],
        color: theme.palette.grey[700],
        border: theme.palette.grey[400],
      }
  }
}

const JobDetailContent = ({ job, rankedJob, conversationId, onFavoriteClick, onAskStethi, isMobile }) => {
  const theme = useTheme()
  const matchTier = rankedJob?.matchTier
  const justification = rankedJob?.justification
  const matchTierColors = getMatchTierColor(matchTier, theme)
  const [applicationDrawerOpen, setApplicationDrawerOpen] = useState(false)

  const feedbackElements = useJobFeedback({
    jobId: job?.id,
    conversationId,
    chatbotMessageId: rankedJob?.chatbotMessageId,
    matchTier,
    feedback: rankedJob?.feedback,
  })

  if (!job) return null

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      {/* Match Tier Badge - desktop only (mobile shows inline in header) */}
      {matchTier && !isMobile && (
        <Box>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
            label={matchTier}
            size='medium'
            sx={{
              backgroundColor: matchTierColors.bg,
              color: matchTierColors.color,
              borderColor: matchTierColors.border,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              '& .MuiChip-icon': {
                color: matchTierColors.color,
              },
            }}
          />
        </Box>
      )}

      {/* Header */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            {isMobile && matchTier && (
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '0.75rem !important' }} />}
                label={matchTier}
                size='small'
                sx={{
                  backgroundColor: matchTierColors.bg,
                  color: matchTierColors.color,
                  borderColor: matchTierColors.border,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  mb: 0.5,
                  '& .MuiChip-icon': {
                    color: matchTierColors.color,
                  },
                }}
              />
            )}
            <Typography variant='h4' sx={{ color: theme.palette.primary.main, mb: 1, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
              {job.title}
            </Typography>
          </Box>
          {onFavoriteClick && (
            <IconButton
              aria-label='favorite'
              onClick={(e) => {
                e.stopPropagation()
                onFavoriteClick({
                  jobId: job.id,
                  favorite: job.candidateJob ? !job.candidateJob.favorite : true,
                })
              }}
            >
              {job.candidateJob?.favorite
                ? <FavoriteIcon color='error' />
                : <FavoriteBorderIcon color='error' />
              }
            </IconButton>
          )}
        </Box>
        {job.employer?.name && (
          <Typography variant='h6' color='text.secondary'>
            {job.employer.name}
          </Typography>
        )}
        {isMobile ? (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {job.createdAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize='small' color='action' />
                  <Typography variant='body2' color='text.secondary'>
                    Posted {new Date(job.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              {job.link && (
                <IconButton
                  size='small'
                  onClick={() => window.open(job.link, '_blank')}
                  aria-label='View Original Posting'
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <JobActionsButton
                jobId={job.id}
                candidateRating={job.candidateRating}
                handleDrawerOpen={setApplicationDrawerOpen}
                mobile={isMobile}
              />
              {onAskStethi && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={onAskStethi}
                  sx={{
                    borderColor: theme.palette.ai?.main || theme.palette.secondary.main,
                    color: theme.palette.ai?.main || theme.palette.secondary.main,
                    '&:hover': {
                      borderColor: theme.palette.ai?.dark || theme.palette.secondary.dark,
                      backgroundColor: `${theme.palette.ai?.main || theme.palette.secondary.main}10`,
                    },
                  }}
                >
                  Ask Stethi
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
            {job.createdAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize='small' color='action' />
                <Typography variant='body2' color='text.secondary'>
                  Posted {new Date(job.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            {job.link && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(job.link, '_blank')}
              >
                View Original Posting
              </Button>
            )}
            <JobActionsButton
              jobId={job.id}
              candidateRating={job.candidateRating}
              handleDrawerOpen={setApplicationDrawerOpen}
              mobile={isMobile}
            />
            {onAskStethi && (
              <Tooltip title="Ask Stethi about this job">
                <IconButton
                  onClick={onAskStethi}
                  sx={{
                    color: theme.palette.ai?.main || theme.palette.secondary.main,
                    '&:hover': {
                      backgroundColor: `${theme.palette.ai?.main || theme.palette.secondary.main}10`,
                    },
                  }}
                >
                  <AutoAwesomeIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* AI Justification - Mobile only */}
      {justification && (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box
            sx={{
              backgroundColor: `${matchTierColors.bg}`,
              borderRadius: 1,
              p: 1.5,
              borderLeft: `3px solid ${matchTierColors.border}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography
                variant='subtitle2'
                sx={{
                  color: matchTierColors.color,
                  fontWeight: 'bold',
                }}
              >
                Why this match
              </Typography>
              {feedbackElements?.thumbs}
            </Box>
            <Typography variant='body2' sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
              {justification}
            </Typography>
          </Box>

          {feedbackElements?.panel}
        </Box>
      )}

      {/* AI Feedback without justification - Mobile only */}
      {!justification && rankedJob && (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box
            sx={{
              backgroundColor: `${matchTierColors.bg}`,
              borderRadius: 1,
              p: 1.5,
              borderLeft: `3px solid ${matchTierColors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                color: matchTierColors.color,
                fontWeight: 'bold',
              }}
            >
              Rate this match
            </Typography>
            {feedbackElements?.thumbs}
          </Box>

          {feedbackElements?.panel}
        </Box>
      )}

      {/* Main Info - CSS Grid on mobile, MUI Grid on desktop */}
      {isMobile ? (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', alignItems: 'baseline' }}>
            {(job.city || job.state) && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Location</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize='small' color='action' />
                  <Typography variant='body2'>
                    {job.city}{job.city && job.state && ', '}{job.state}
                  </Typography>
                </Box>
              </>
            )}
            {formatSalary(job.salary || job.salaryMin || job.salaryMax) && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Salary</Typography>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoneyIcon fontSize='small' color='action' />
                    <Typography variant='body2'>
                      {formatSalary(job.salary || job.salaryMin || job.salaryMax)}
                    </Typography>
                  </Box>
                  {formatSalary(job.salaryMin) && formatSalary(job.salaryMax) && (
                    <Typography variant='caption' color='text.secondary' sx={{ ml: 2.5 }}>
                      Range: {formatSalary(job.salaryMin)} - {formatSalary(job.salaryMax)}
                    </Typography>
                  )}
                </Box>
              </>
            )}
            {job.practiceType && getPracticeTypeString(job.practiceType) && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Practice</Typography>
                <Typography variant='body2'>{getPracticeTypeString(job.practiceType)}</Typography>
              </>
            )}
            {job.candidateType?.name && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Position</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SchoolIcon fontSize='small' color='action' />
                  <Typography variant='body2'>{job.candidateType.name}</Typography>
                </Box>
              </>
            )}
            {job.areaType && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Area</Typography>
                <Typography variant='body2'>{job.areaType}</Typography>
              </>
            )}
            {job.hours && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Hours</Typography>
                <Typography variant='body2'>{job.hours}</Typography>
              </>
            )}
            {job.shiftType && (
              <>
                <Typography variant='subtitle2' color='text.secondary'>Shift</Typography>
                <Typography variant='body2'>{job.shiftType}</Typography>
              </>
            )}
          </Box>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {job.specialties?.length > 0 && (
              <Box>
                <Typography variant='subtitle2' color='text.secondary'>Specialties</Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap' gap={0.5} sx={{ mt: 0.5 }}>
                  {job.specialties.map((specialty) => (
                    <Chip
                      key={specialty?.id}
                      label={specialty?.name}
                      size='small'
                      sx={{ backgroundColor: theme.palette.primary.light }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {job.subspecialties?.length > 0 && (
              <Box>
                <Typography variant='subtitle2' color='text.secondary'>Subspecialties</Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap' gap={0.5} sx={{ mt: 0.5 }}>
                  {job.subspecialties.map((subspecialty) => (
                    <Chip
                      key={subspecialty?.id}
                      label={subspecialty?.name}
                      variant='outlined'
                      size='small'
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {(job.visaSponsorship || job.loanForgiveness || job.pslfCompatible) && (
              <Box>
                <Typography variant='subtitle2' color='text.secondary'>Benefits & Eligibility</Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap' gap={0.5} sx={{ mt: 0.5 }}>
                  {job.visaSponsorship && (
                    <Chip label='Visa Sponsorship Available' color='success' variant='filled' size='small' />
                  )}
                  {job.loanForgiveness && (
                    <Chip label='Loan Forgiveness Available' color='success' variant='filled' size='small' />
                  )}
                  {job.pslfCompatible && (
                    <Chip label='PSLF Eligible' color='success' variant='filled' size='small' />
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {(job.city || job.state) && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Location
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon color='action' />
                    <Typography variant='body1'>
                      {job.city}{job.city && job.state && ', '}{job.state}
                    </Typography>
                  </Box>
                </Box>
              )}

              {formatSalary(job.salary || job.salaryMin || job.salaryMax) && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Salary
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoneyIcon color='action' />
                    <Typography variant='body1'>
                      {formatSalary(job.salary || job.salaryMin || job.salaryMax)}
                    </Typography>
                  </Box>
                  {formatSalary(job.salaryMin) && formatSalary(job.salaryMax) && (
                    <Typography variant='body2' color='text.secondary' sx={{ ml: 3 }}>
                      Range: {formatSalary(job.salaryMin)} - {formatSalary(job.salaryMax)}
                    </Typography>
                  )}
                </Box>
              )}

              {job.practiceType && getPracticeTypeString(job.practiceType) && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Practice Type
                  </Typography>
                  <Typography variant='body1'>
                    {getPracticeTypeString(job.practiceType)}
                  </Typography>
                </Box>
              )}

              {job.candidateType?.name && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Position Type
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon color='action' />
                    <Typography variant='body1'>
                      {job.candidateType.name}
                    </Typography>
                  </Box>
                </Box>
              )}

              {job.areaType && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Area Type
                  </Typography>
                  <Typography variant='body1'>
                    {job.areaType}
                  </Typography>
                </Box>
              )}

              {job.hours && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Hours
                  </Typography>
                  <Typography variant='body1'>
                    {job.hours}
                  </Typography>
                </Box>
              )}

              {job.shiftType && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Shift Type
                  </Typography>
                  <Typography variant='body1'>
                    {job.shiftType}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {job.specialties?.length > 0 && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Specialties
                  </Typography>
                  <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                    {job.specialties.map((specialty) => (
                      <Chip
                        key={specialty?.id}
                        label={specialty?.name}
                        sx={{ backgroundColor: theme.palette.primary.light }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {job.subspecialties?.length > 0 && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Subspecialties
                  </Typography>
                  <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                    {job.subspecialties.map((subspecialty) => (
                      <Chip
                        key={subspecialty?.id}
                        label={subspecialty?.name}
                        variant='outlined'
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {(job.visaSponsorship || job.loanForgiveness || job.pslfCompatible) && (
                <Box>
                  <Typography variant='subtitle2' color='text.secondary'>
                    Benefits & Eligibility
                  </Typography>
                  <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                    {job.visaSponsorship && (
                      <Chip
                        label='Visa Sponsorship Available'
                        color='success'
                        variant='filled'
                        size='small'
                      />
                    )}
                    {job.loanForgiveness && (
                      <Chip
                        label='Loan Forgiveness Available'
                        color='success'
                        variant='filled'
                        size='small'
                      />
                    )}
                    {job.pslfCompatible && (
                      <Chip
                        label='PSLF Eligible'
                        color='success'
                        variant='filled'
                        size='small'
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      )}

      {/* Contact Information */}
      {(job.employerUser || job.user) && (
        <>
          <Divider sx={{ opacity: isMobile ? 0.5 : 1 }} />
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: theme.palette.primary.main, mb: isMobile ? 1 : 2, fontWeight: 600 }}>
              Contact Information
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 1.5 : 2,
              p: isMobile ? 1.5 : 2,
              borderRadius: 1,
              backgroundColor: theme.palette.grey[50]
            }}>
              <Avatar
                sx={{
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                  bgcolor: theme.palette.primary.main,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}
              >
                {(job.employerUser?.user?.firstName?.[0] || job.user?.firstName?.[0] || '')}
                {(job.employerUser?.user?.lastName?.[0] || job.user?.lastName?.[0] || '')}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 'medium' }}>
                  {job.employerUser?.user?.firstName || job.user?.firstName} {job.employerUser?.user?.lastName || job.user?.lastName}
                </Typography>
                {job.employerUser?.jobTitle && (
                  <Typography variant='body2' color='text.secondary'>
                    {job.employerUser.jobTitle}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  {(job.employerUser?.user?.showEmail || job.user?.showEmail) && (job.employerUser?.user?.email || job.user?.email) && (
                    <Button
                      size='small'
                      startIcon={<EmailIcon />}
                      onClick={() => window.open(`mailto:${job.employerUser?.user?.email || job.user?.email}`)}
                    >
                      Email
                    </Button>
                  )}
                  {(job.employerUser?.user?.showPhone || job.user?.showPhone) && (job.employerUser?.user?.phone || job.user?.phone) && (
                    <Button
                      size='small'
                      startIcon={<PhoneIcon />}
                      onClick={() => window.open(`tel:${job.employerUser?.user?.phone || job.user?.phone}`)}
                    >
                      Call
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Job Description */}
      {(job.about || job.aboutRaw) && (
        <>
          <Divider sx={{ opacity: isMobile ? 0.5 : 1 }} />
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: theme.palette.primary.main, mb: isMobile ? 1 : 2, fontWeight: 600 }}>
              Job Description
            </Typography>
            <Typography
              variant='body1'
              sx={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(convertNewlinesToBreaks(job.about || job.aboutRaw) || '')
              }}
            />
          </Box>
        </>
      )}

      {/* Employer Description */}
      {job.employer?.about && (
        <>
          <Divider sx={{ opacity: isMobile ? 0.5 : 1 }} />
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: theme.palette.primary.main, mb: isMobile ? 1 : 2, fontWeight: 600 }}>
              About {job.employer?.name || 'the Employer'}
            </Typography>
            <Typography
              variant='body1'
              sx={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(convertNewlinesToBreaks(job.employer.about) || '')
              }}
            />
          </Box>
        </>
      )}

      {/* Additional Information */}
      {job.academicType && (
        <>
          <Divider sx={{ opacity: isMobile ? 0.5 : 1 }} />
          <Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ color: theme.palette.primary.main, mb: isMobile ? 1 : 2, fontWeight: 600 }}>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Academic Type
                </Typography>
                <Typography variant='body2'>
                  {job.academicType}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      <ApplicationDrawer
        open={applicationDrawerOpen}
        onClose={() => setApplicationDrawerOpen(false)}
        job={job}
        jobs={[]}
      />
    </Stack>
  )
}

export default JobDetailContent
